import deepmerge from 'deepmerge';
import jss, { JssStyle, StyleSheet } from 'jss';
import preset from 'jss-preset-default';
import { ElementID, Scene } from '../../../enums/Checkout';

import {
  AlternativePaymentMethodButtonStyle,
  BackButtonStyle,
  BaseInputStyle,
  BlockStyle,
  CheckoutStyle,
  DirectDebitMandateStyle,
  DirectDebitSuccessStyle,
  EditButtonStyle,
  ErrorMessageStyle,
  FormSpacings,
  InputStyle,
  InputStyles,
  LoadingScreenStyle,
  SavedPaymentMethodButtonStyle,
  SavedPaymentMethodButtonStyles,
  SeparatorStyle,
  ShowMorePaymentMethodsButtonStyles,
  SubmitButtonStyle,
  SubmitButtonStyles,
  TextAlignmentStyle,
  TextStyle,
  VaultMenuStyle,
} from '../../../styles';
import { IStyleManager } from '../StyleManager';

///////////////////////////////////////////
// Utils
///////////////////////////////////////////
const baseClassName = '.PrimerCheckout';

const className = (name: string) => `${baseClassName}__${name}`;
const state = (name: string) => `${baseClassName}--${name}`;

///////////////////////////////////////////
// Conversion to JSS
///////////////////////////////////////////
const getTextStyle = (style?: TextStyle): JssStyle => {
  return {
    color: style?.color,
    fontFamily: style?.fontFamily,
    fontWeight: style?.fontWeight,
    fontSize: style?.fontSize,
    fontSmoothing: style?.fontSmoothing,
    lineHeight: style?.lineHeight,
  } as JssStyle;
};

const getBlockStyle = (style?: BlockStyle): JssStyle => {
  return {
    background: style?.background,
    borderStyle: style?.borderStyle,
    borderWidth: style?.borderWidth,
    borderColor: style?.borderColor,
    borderRadius: style?.borderRadius,
    boxShadow: style?.boxShadow,
  } as JssStyle;
};

const getBaseInputStyle = (style?: BaseInputStyle): JssStyle => {
  return {
    ...getTextStyle(style),
    ...getBlockStyle(style),
    height: style?.height,
    paddingLeft: style?.paddingHorizontal,
    paddingRight: style?.paddingHorizontal,
  } as JssStyle;
};

const getHostedFieldInputStyles = (style?: InputStyle): JssStyle => {
  return {
    ...getTextStyle(style),
    '&:hover': {
      ...getTextStyle(style?.hover),
    },
    '&:focus': {
      ...getTextStyle(style?.focus),
    },
    '&::placeholder': {
      ...getTextStyle(style?.placeholder),
    },
    '&::selection': {
      ...getTextStyle(style?.selection),
    },
  };
};

export const getJSSHostedFieldStyle = (style?: CheckoutStyle): JssStyle => {
  return {
    '@font-face': style?.fontFaces as any,

    '@global': {
      [`input`]: {
        ...getHostedFieldInputStyles(style?.input?.base),
        'input:-webkit-autofill, input:-webkit-autofill:input:focus, input:-webkit-autofill:hover': {
          ...getTextStyle(style?.input?.base?.webkitAutofill),
        },
      },

      // Error + Submitted
      '#primer-hosted-form.submitted': {
        '& input': {
          '&.error': {
            ...getHostedFieldInputStyles(style?.input?.error),
          },
        },
      },
    },
  };
};

const getInputStyle = (style?: InputStyle): JssStyle => {
  return {
    ...getBaseInputStyle(style),
    '&:hover': {
      ...getBaseInputStyle(style?.hover),
    },
    [`&:focus, &${state('focused')}`]: {
      ...getBaseInputStyle(style?.focus),
    },
    '&::placeholder': {
      ...getBaseInputStyle(style?.placeholder),
    },
    '&::selection': {
      ...getBaseInputStyle(style?.selection),
    },
    '&:-webkit-autofill, &:-webkit-autofill:focus, &:-webkit-autofill:hover': {
      ...getBaseInputStyle(style?.webkitAutofill),
    },
  };
};

const getInputStyles = (style?: InputStyles): JssStyle => {
  const borderWidthMargin = style?.base?.borderWidth
    ? -style.base.borderWidth
    : undefined;

  return {
    // Input states
    [`${baseClassName} ${className('formInput')}`]: getInputStyle(style?.base),
    // Error
    [`${baseClassName} ${state('error')}`]: {
      [`& ${className('formInput')}`]: getInputStyle(style?.error),
    },

    [`${baseClassName} ${className('formInputs')}`]: {
      [`& ${className('formInput')}`]: {
        borderRadius: '0px',
      },
    },

    // Multiple inputs round
    [`${baseClassName} ${className('formInputs--vertical')}`]: {
      '& > *:first-child': {
        borderTopLeftRadius: style?.base?.borderRadius,
        borderTopRightRadius: style?.base?.borderRadius,
      },
      '& > *:last-child': {
        borderBottomLeftRadius: style?.base?.borderRadius,
        borderBottomRightRadius: style?.base?.borderRadius,
      },
      '& > .PrimerCheckout__formInputs--horizontal:last-child': {
        '& > *:first-child': {
          borderBottomLeftRadius: style?.base?.borderRadius,
        },
        '& > *:last-child': {
          borderBottomRightRadius: style?.base?.borderRadius,
        },
      },

      // Multiple inputs border width
      [`& ${className('formInput')} + ${className('formInput')} `]: {
        marginTop: borderWidthMargin,
      },
    },
    // Multiple inputs border width
    [`${baseClassName} ${className('formInputs--horizontal')}`]: {
      [`& ${className('formInput')}`]: {
        marginTop: borderWidthMargin,
      },
      [`& ${className('formInput')} + ${className('formInput')} `]: {
        marginLeft: borderWidthMargin,
      },
    },
  } as JssStyle;
};

const getSubmitButtonStyle = (style?: SubmitButtonStyle) => {
  return {
    ...getTextStyle(style),
    ...getBlockStyle(style),
  };
};

const getSubmitButtonStyles = (style?: SubmitButtonStyles): JssStyle => {
  return {
    // Base
    [`${baseClassName} ${className('submitButton')}`]: {
      ...getSubmitButtonStyle(style?.base),
    },

    // Loading state
    [`${baseClassName} ${state('loading')} ${className('submitButton')}`]: {
      ...getSubmitButtonStyle(style?.loading),
    },

    // Loading indicator
    [`${baseClassName} ${className('loadingRing')} .spinner .path`]: {
      stroke: style?.loading?.color ?? style?.base?.color,
    },

    // Disabled
    [`${baseClassName} ${className('submitButton')}:disabled`]: {
      ...getSubmitButtonStyle(style?.disabled),
    },
  } as JssStyle;
};

const getLoadingScreenStyle = (style?: LoadingScreenStyle): JssStyle => {
  return {
    [`${baseClassName} ${className('spinner')} .spinner .path`]: {
      stroke: style?.color,
    },
  } as JssStyle;
};

const getShowMorePaymentMethodsButtonStyles = (
  style?: ShowMorePaymentMethodsButtonStyles,
): JssStyle => {
  return {
    [`${baseClassName} #${ElementID.NAVIGATE_PAYMENT_METHODS}`]: {
      [`& ${className('label--primary')}`]: getTextStyle({
        color: style?.base?.color,
      }),

      // Share the base with the disabled (except color)
      [`& ${className('label')}`]: getTextStyle({
        ...style?.base,
        color: undefined,
      }),

      [`&:disabled ${className('label')}`]: getTextStyle(style?.disabled),
    },
  } as JssStyle;
};

const getInputLabelStyle = (style?: TextStyle) => {
  return {
    [`${baseClassName} label${className('label')}`]: getTextStyle(style),
  } as JssStyle;
};

const getSavedPaymentButtonStyle = (style?: SavedPaymentMethodButtonStyle) => {
  return {
    [`& ${className('label')}`]: {
      ...getTextStyle(style),
    },

    ...getBlockStyle(style),
    '&:hover': {
      [`& ${className('label')}`]: {
        ...getTextStyle(style?.hover),
      },
      ...getBlockStyle(style?.hover),
    },
    '&:focus': {
      [`& ${className('label')}`]: {
        ...getTextStyle(style?.focus),
      },
      ...getBlockStyle(style?.focus),
    },
  } as JssStyle;
};

const getSavedPaymentButtonStyles = (
  style?: SavedPaymentMethodButtonStyles,
) => {
  return {
    [`${baseClassName} ${className('savedPaymentMethod')}`]: {
      ...getSavedPaymentButtonStyle(style?.base),
      [`&${state('selected')}`]: {
        ...getSavedPaymentButtonStyle(
          deepmerge(style?.base ?? {}, style?.selected ?? {}),
        ),
      },
    },
  } as JssStyle;
};

const getInputErrorTextStyle = (
  style?: TextStyle & TextAlignmentStyle,
): JssStyle => {
  return {
    [`${baseClassName} ${className('helperText--error')}`]: {
      ...getTextStyle(style),
      textAlign: style?.textAlign,
    },
  } as JssStyle;
};

const getDirectDebitMandateStyle = (
  style?: DirectDebitMandateStyle,
): JssStyle => {
  return {
    [`${baseClassName} #${Scene.DIRECT_DEBIT_MANDATE}`]: {
      [`& ${className('title')}`]: getTextStyle(style?.header),
      [`& ${className('mandateItem')}`]: {
        '& .icon': {
          color: style?.label?.color,
        },
      },
      [`& ${className('mandateDetail')}`]: {
        '& > *:first-child': getTextStyle(style?.label),
        '& > *:last-child': getTextStyle(style?.content),
      },
    },
    [`#primer-checkout-dd-creditor-details`]: getTextStyle(
      style?.creditorDetails,
    ),
  } as JssStyle;
};

const getDirectDebitSuccessStyle = (
  style?: DirectDebitSuccessStyle,
): JssStyle => {
  return {
    [`${baseClassName} ${className('directDebitSuccess')}`]: {
      [`& ${className('directDebitSuccessIcon')} > ${className('icon')}`]: {
        color: style?.icon?.color,
      },
    },
  } as JssStyle;
};

const getSmallPrintStyle = (style?: TextStyle): JssStyle => {
  return {
    [`${baseClassName} #primer-checkout-small-print`]: getTextStyle(style),
  };
};

const getAlternativePaymentMethodButtonStyle = (
  style?: AlternativePaymentMethodButtonStyle,
): JssStyle => {
  return {
    [`${baseClassName} ${className('apmButton')}`]: {
      height: style?.height,
      borderRadius: style?.borderRadius,
    },
    [`${baseClassName} ${className('apmButton--managed')}`]: {
      '& button': getBlockStyle(style),
    },
  } as JssStyle;
};

const getBackButtonStyle = (style?: BackButtonStyle): JssStyle => {
  return {
    [`${baseClassName} ${className('backButton')}`]: {
      color: style?.color,
    },
  } as JssStyle;
};

const getSeparatorStyle = (style?: SeparatorStyle): JssStyle => {
  return {
    [`${baseClassName} ${className('textDivider')}`]: {
      '&::before, &::after': {
        background: style?.color,
      },
    },
    [`${baseClassName} #primer-checkout-dd-creditor-details`]: {
      borderTopColor: style?.color,
      borderBottomColor: style?.color,
    },
  } as JssStyle;
};

const getErrorMessageStyle = (style?: ErrorMessageStyle): JssStyle => {
  return {
    [`${baseClassName} ${className('errorMessage')}`]: {
      ...getTextStyle(style),
      ...getBlockStyle(style),
    },
  };
};

const getFormSpacings = (style?: FormSpacings): JssStyle => {
  return {
    [`${baseClassName} ${className('formField')}`]: {
      '&:not(:first-of-type)': {
        marginTop: style?.betweenInputs,
      },
      [`& > ${className('label')}`]: {
        marginBottom: style?.betweenLabelAndInput,
      },
    },
    [`${baseClassName} ${className('formField--dual')} > ${className(
      'formField',
    )}`]: {
      marginTop: 0,
    },
  } as JssStyle;
};

const getVaultMenuStyle = (style?: VaultMenuStyle): JssStyle => {
  return {
    [`${baseClassName} ${className('deleteButton')}`]: getTextStyle(
      style?.item?.actionButton,
    ),
    [`${baseClassName} ${className('confirmButton')}`]: {
      ...getTextStyle(style?.item?.confirmButton),
      ...getBlockStyle(style?.item?.confirmButton),
    },
    [`${baseClassName} ${className(
      'paymentMethodDropDownMenuItem__label',
    )}${className('label')}`]: getTextStyle(style?.item?.label),
    [`${baseClassName} ${className('editButton')}`]: {
      backgroundColor: style?.editButton?.background,
    },
    [`${baseClassName} ${className('editButton__icon')}`]: {
      fill: style?.editButton?.color,
    },
  } as JssStyle;
};

const getVaultTitleStyle = (style?: TextStyle): JssStyle => {
  return {
    [`${baseClassName} ${className('savedPaymentMethods__label')}${className(
      'label',
    )}`]: getTextStyle(style),
  };
};

export const getJSSGlobalStyle = (style?: CheckoutStyle): JssStyle => {
  return {
    '@global': {
      ...getLoadingScreenStyle(style?.loadingScreen),
      ...getInputStyles(style?.input),
      ...getInputLabelStyle(style?.inputLabel),
      ...getInputErrorTextStyle(style?.inputErrorText),
      ...getShowMorePaymentMethodsButtonStyles(
        style?.showMorePaymentMethodsButton,
      ),
      ...getAlternativePaymentMethodButtonStyle(
        style?.alternativePaymentMethodButton,
      ),
      ...getFormSpacings(style?.formSpacings),
      ...getSubmitButtonStyles(style?.submitButton),
      ...getSavedPaymentButtonStyles(style?.savedPaymentMethodButton),
      ...getDirectDebitMandateStyle(style?.directDebit?.mandate),
      ...getDirectDebitSuccessStyle(style?.directDebit?.success),
      ...getSmallPrintStyle(style?.smallPrint),
      ...getBackButtonStyle(style?.backButton),
      ...getSeparatorStyle(style?.separator),
      ...getErrorMessageStyle(style?.errorMessage),
      ...getVaultMenuStyle(style?.vaultMenu),
      ...getVaultTitleStyle(style?.vaultTitle),
    },
  };
};

///////////////////////////////////////////
// Manager
///////////////////////////////////////////

export class JSSStyleManager implements IStyleManager {
  private sheet: StyleSheet | null;

  private hostedFieldStyle: string | null;

  private style: CheckoutStyle | null;

  constructor() {
    this.sheet = null;
    this.hostedFieldStyle = null;
  }

  setStyle(style?: CheckoutStyle): void {
    this.style = style ?? null;
    jss.setup(preset());

    this.detachIfNeeded();

    const jssHostedFieldStyle = getJSSHostedFieldStyle(style);
    const jssStyle = getJSSGlobalStyle(style);

    this.sheet = jss.createStyleSheet({ '@global': jssStyle });
    this.sheet.attach();

    this.hostedFieldStyle = jss
      .createStyleSheet({ '@global': jssHostedFieldStyle })
      .toString();
  }

  getHostedFieldStyle(): string | null {
    return this.hostedFieldStyle;
  }

  getApmButtonStyle(): AlternativePaymentMethodButtonStyle | null {
    return this.style?.alternativePaymentMethodButton ?? null;
  }

  private detachIfNeeded() {
    if (this.sheet) {
      this.sheet.detach();
      this.sheet = null;
      this.hostedFieldStyle = null;
    }
  }
}
